import React, { useState, useEffect } from 'react';
import { db } from '../firebase/firebase-config'; // Asegúrate que la ruta sea correcta
import { collection, onSnapshot, getDocs } from "firebase/firestore";
import { getFunctions, httpsCallable } from 'firebase/functions';
import { chipClassForRole, roleTooltip } from '../utils/roleTaxonomy';

// --- Iconos (sin cambios) ---
const UserPlusIcon = () => ( <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg> );
const EditIcon = () => ( <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.5L15.232 5.232z" /></svg> );
const DeleteIcon = () => ( <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg> );
const XIcon = () => ( <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg> );

// Lista de respaldo por si la colección `roles` aún no está poblada.
const fallbackRoles = [ 'Admin', 'Diseñador', 'Jefe de Producción', 'Operador de Máquina', 'Prepress', 'Ventas', 'Partner', 'Finishing', 'Instalador' ];

export default function UsersView() {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [availableRoles, setAvailableRoles] = useState(fallbackRoles);
  const functions = getFunctions();

  useEffect(() => {
    const usersCollectionRef = collection(db, 'users');
    const unsubscribe = onSnapshot(usersCollectionRef, (snapshot) => {
      const usersList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setUsers(usersList);
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Carga los roles reales desde la colección `roles` (se enlazan por nombre).
  useEffect(() => {
    (async () => {
      try {
        const snapshot = await getDocs(collection(db, 'roles'));
        const roleNames = snapshot.docs.map(doc => doc.data().name).filter(Boolean);
        if (roleNames.length > 0) setAvailableRoles(roleNames.sort());
      } catch (error) {
        console.error("Error al cargar los roles:", error);
      }
    })();
  }, []);

  const handleCreateUser = async (newUser) => {
    try {
      const createUser = httpsCallable(functions, 'createNewUser');
      await createUser(newUser);
    } catch (error) {
      console.error("Error al crear usuario:", error);
      alert(`Error: ${error.message}`);
    }
  };
  
  const handleEditUser = (user) => {
    setEditingUser(user);
    setIsEditModalOpen(true);
  };

  const handleUpdateUser = async (updatedData) => {
    try {
      const updateUser = httpsCallable(functions, 'updateUser');
      await updateUser(updatedData);
      // La lista se refresca sola gracias al onSnapshot en tiempo real.
    } catch (error) {
      console.error("Error al actualizar usuario:", error);
      alert(`No se pudo actualizar el usuario: ${error.message}`);
      throw error; // para que el modal sepa que falló y no se cierre
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (window.confirm(`¿Estás seguro de que quieres eliminar a ${userEmail}? Esta acción es irreversible.`)) {
      try {
        const deleteUser = httpsCallable(functions, 'deleteUserFromSystem');
        await deleteUser({ uid: userId });
      } catch (error) {
        console.error("Error al eliminar usuario:", error);
        alert(`Error: ${error.message}`);
      }
    }
  };

  return (
    <div className="p-8 bg-gray-50 min-h-screen font-sans">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Gestión de Usuarios</h1>
        <button onClick={() => setIsModalOpen(true)} className="flex items-center justify-center bg-blue-600 text-white font-bold py-2 px-4 rounded-lg shadow-md hover:bg-blue-700 transition">
          <UserPlusIcon /> Añadir Nuevo Usuario
        </button>
      </div>
      <p className="text-gray-600 mb-8">Administra el acceso y los roles de todos los miembros del equipo.</p>
      <div className="bg-white shadow-lg rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Nombre</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-600 uppercase">Roles</th>
                <th className="px-6 py-3 text-center text-xs font-bold text-gray-600 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr><td colSpan="4" className="text-center py-4">Cargando usuarios...</td></tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.displayName || user.name
                        ? <div className="text-sm font-medium text-gray-900">{user.displayName || user.name}</div>
                        : <div className="text-sm italic text-gray-400">Sin nombre</div>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap"><div className="text-sm text-gray-500">{user.email}</div></td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-wrap gap-2">
                        {(user.roles || []).map(role => ( <span key={role} title={roleTooltip(role)} className={`px-2.5 py-0.5 text-xs font-semibold rounded-full ${chipClassForRole(role)}`}>{role}</span> ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-center">
                      <div className="flex items-center justify-center space-x-4">
                        <button onClick={() => handleEditUser(user)} className="text-blue-600 hover:text-blue-900" title="Editar"><EditIcon /></button>
                        <button onClick={() => handleDeleteUser(user.id, user.email)} className="text-red-600 hover:text-red-900" title="Eliminar"><DeleteIcon /></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      {isModalOpen && (
        <AddUserModal onClose={() => setIsModalOpen(false)} onCreateUser={handleCreateUser} availableRoles={availableRoles} />
      )}
      {isEditModalOpen && editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => { setIsEditModalOpen(false); setEditingUser(null); }}
          onUpdateUser={handleUpdateUser}
          availableRoles={availableRoles}
        />
      )}
    </div>
  );
}

function AddUserModal({ onClose, onCreateUser, availableRoles }) {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [selectedRoles, setSelectedRoles] = useState([]);

    const handleRoleChange = (role) => { setSelectedRoles(prev => prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]); };
    const handleSubmit = (e) => { e.preventDefault(); onCreateUser({ displayName: name, email, password, roles: selectedRoles }); onClose(); };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50">
            <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-lg relative">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><XIcon /></button>
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Añadir Nuevo Usuario</h2>
                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label className="block text-sm font-bold text-gray-600 mb-1">Nombre Completo</label>
                        <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full p-3 border rounded-lg" required />
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-bold text-gray-600 mb-1">Correo Electrónico</label>
                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full p-3 border rounded-lg" required />
                    </div>
                    <div className="mb-6">
                        <label className="block text-sm font-bold text-gray-600 mb-1">Contraseña Temporal</label>
                        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full p-3 border rounded-lg" required />
                    </div>
                    <div className="mb-8">
                        <label className="block text-sm font-bold text-gray-600 mb-3">Asignar Roles</label>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            {availableRoles.map(role => (
                                <label key={role} className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                                    <input type="checkbox" checked={selectedRoles.includes(role)} onChange={() => handleRoleChange(role)} className="h-5 w-5 rounded" />
                                    <span className="ml-3 text-sm font-medium text-gray-700">{role}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                    <div className="flex justify-end gap-4">
                        <button type="button" onClick={onClose} className="py-2 px-6 font-bold bg-gray-200 rounded-lg">Cancelar</button>
                        <button type="submit" className="py-2 px-6 font-bold text-white bg-blue-600 rounded-lg">Crear Usuario</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function EditUserModal({ user, onClose, onUpdateUser, availableRoles }) {
    const [name, setName] = useState(user.displayName || '');
    const [selectedRoles, setSelectedRoles] = useState(user.roles || []);
    const [isSaving, setIsSaving] = useState(false);

    const handleRoleChange = (role) => { setSelectedRoles(prev => prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]); };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            await onUpdateUser({ uid: user.id, displayName: name.trim(), roles: selectedRoles });
            onClose();
        } catch (error) {
            // El mensaje amigable ya lo mostró el handler; mantenemos el modal abierto.
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50">
            <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-lg relative">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><XIcon /></button>
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Editar Usuario</h2>
                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label className="block text-sm font-bold text-gray-600 mb-1">Nombre Completo</label>
                        <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full p-3 border rounded-lg" required />
                    </div>
                    <div className="mb-6">
                        <label className="block text-sm font-bold text-gray-600 mb-1">Correo Electrónico</label>
                        <input type="email" value={user.email || ''} className="w-full p-3 border rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed" disabled readOnly />
                        <p className="text-xs text-gray-400 mt-1">El correo es el identificador en Firebase Auth y no se puede modificar.</p>
                    </div>
                    <div className="mb-8">
                        <label className="block text-sm font-bold text-gray-600 mb-3">Roles Asignados</label>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                            {availableRoles.map(role => (
                                <label key={role} className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                                    <input type="checkbox" checked={selectedRoles.includes(role)} onChange={() => handleRoleChange(role)} className="h-5 w-5 rounded" />
                                    <span className="ml-3 text-sm font-medium text-gray-700">{role}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                    <div className="flex justify-end gap-4">
                        <button type="button" onClick={onClose} className="py-2 px-6 font-bold bg-gray-200 rounded-lg">Cancelar</button>
                        <button type="submit" disabled={isSaving} className="py-2 px-6 font-bold text-white bg-blue-600 rounded-lg disabled:opacity-60 disabled:cursor-not-allowed">{isSaving ? 'Guardando...' : 'Guardar Cambios'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
}
